import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '../lib/analytics';
import type { UniverseSort as SortValue } from '../lib/types';
import { UniverseHeader } from './UniverseHeader';
import { UniverseSearch } from './UniverseSearch';
import { UniverseSort } from './UniverseSort';

interface Props {
  variant: 'masthead' | 'compact';
  query: string;
  sort: SortValue;
  sortOptions: readonly SortValue[];
  onSortChange: (value: SortValue) => void;
}

function isVisible(el: HTMLElement | null): el is HTMLElement {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < window.innerHeight && getComputedStyle(el).visibility !== 'hidden';
}

/** Header + search + sort wiring shared by every wall page. */
export function UniverseChrome({ variant, query, sort, sortOptions, onSortChange }: Props) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(query);
  const heroRef = useRef<HTMLInputElement>(null);
  const compactRef = useRef<HTMLInputElement>(null);

  useEffect(() => setDraft(query), [query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[data-universe-viewer]')) return;
      if (e.target instanceof Element && e.target.closest('input, textarea, [contenteditable="true"]')) return;
      e.preventDefault();
      const input = isVisible(heroRef.current) ? heroRef.current : compactRef.current;
      input?.focus();
      input?.select();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const submit = (value: string) => {
    const q = value.trim();
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    // Only a shared-link override needs carrying; a saved choice re-applies on its own.
    const shared = new URLSearchParams(window.location.search).get('sort');
    if (shared && shared === sort && sort !== 'relevant') params.set('sort', sort);
    const qs = params.toString();
    // Query text can contain personal data; only its shape is recorded.
    track(q ? 'search_submitted' : 'search_cleared', { query_length: q.length, query_terms: q ? q.split(/\s+/).length : 0, sort });
    navigate(q ? `/search?${qs}` : qs ? `/?${qs}` : '/');
  };

  const clear = () => {
    setDraft('');
    if (query) submit('');
  };

  return (
    <UniverseHeader
      variant={variant}
      renderSearch={(size) => (
        <UniverseSearch
          ref={size === 'hero' ? heroRef : compactRef}
          size={size}
          value={draft}
          onChange={setDraft}
          onSubmit={submit}
          onClear={clear}
        />
      )}
      renderSort={(tone) =>
        sortOptions.length > 1 ? (
          <UniverseSort value={sort} options={sortOptions} onChange={onSortChange} tone={tone} />
        ) : null
      }
    />
  );
}
